/**
 * 토마토 따기 게임 — 서버·클라 공용 코어.
 *
 * 서버는 판 시작 시 **시드만** 내려주고, 클라이언트는 여기 있는 결정론 생성기로
 * 스폰(위치·타이밍·종류)을 만들어 렌더링한다. 서버는 같은 설정으로
 * "익은 토마토 총수"를 알기에 점수 상한을 검증할 수 있다 — 스폰 테이블을
 * 주고받지 않아 가볍다. (근거: docs/토마토_따기_게임_스펙.md)
 *
 * 여기 있는 숫자들은 **기본값**이다. 서버가 env로 덮어써서 세션 시작 응답에
 * 실어 보내므로, 밸런스 튜닝에 배포가 필요 없다. 클라는 항상 응답의 설정을 쓴다.
 */
/** 따면 수확(+1)인가. 나머지는 전부 감점. */
export function isHarvestable(kind) {
    return kind === 'ripe';
}
/** 움직이는 생물인가(등장 후 드리프트로 이동). */
export function isCritter(kind) {
    return kind === 'ladybug' || kind === 'butterfly';
}
/**
 * 기본 밸런스 — 7라운드 난이도 곡선.
 * 익은 토마토 합 **30** → 판당 최대 지급 floor(30×0.5)=15.
 * 하루 만점 기준 6판×15 = 90🍅 (기획 확정값 유지 — 회차만 없앴다).
 *
 * 곡선: 등장 수↑ · 머무는 시간↓ · 함정 종류 추가 · 수풀 우거짐.
 *  R1 튜토리얼 → R2 피망 → R3 반쯤 익은 색 → R4 나비·무당벌레(이후 2배씩 증식) → R6~7 물량전.
 *
 * **난이도 1.7배 상향(2026-08-14)** — 방해 요소(안 익은·반숙·피망·벌레·수풀)만 각각 1.7배로
 * 올렸다. 체류 시간(lifeMin/MaxMs)은 건드리지 않았다: 시간까지 같이 조이면 레버가 곱으로
 * 겹쳐 후반이 사실상 상위자 전용이 된다. 반사신경이 아니라 **식별·정확도** 싸움으로 어렵게 한다.
 * 익은 토마토 수(합 30)는 고정 — 늘리면 판당 지급 상한이 올라 🍅 경제가 바뀐다.
 */
export const DEFAULT_HARVEST_CONFIG = {
    rewardedPlaysPerDay: 6,
    penalty: 2,
    payoutRate: 0.5,
    rounds: [
        // R1은 튜토리얼이라 피망을 넣지 않는다(0×1.7은 여전히 0).
        { durationMs: 8_000, ripe: 3, unripe: 2, half: 0, decoys: 0, critters: 0, lifeMinMs: 2_600, lifeMaxMs: 3_200, foliage: 3 },
        { durationMs: 8_000, ripe: 3, unripe: 3, half: 0, decoys: 2, critters: 0, lifeMinMs: 2_400, lifeMaxMs: 3_000, foliage: 5 },
        { durationMs: 8_000, ripe: 4, unripe: 3, half: 2, decoys: 2, critters: 0, lifeMinMs: 2_200, lifeMaxMs: 2_800, foliage: 7 },
        { durationMs: 9_000, ripe: 4, unripe: 5, half: 2, decoys: 2, critters: 3, lifeMinMs: 2_000, lifeMaxMs: 2_500, foliage: 9 },
        { durationMs: 9_000, ripe: 5, unripe: 5, half: 3, decoys: 2, critters: 7, lifeMinMs: 1_700, lifeMaxMs: 2_200, foliage: 10 },
        // R6~7: 토마토가 우수수 — 많이 나오고 금방 사라진다. 벌레 떼도 계속 2배로.
        { durationMs: 10_000, ripe: 5, unripe: 7, half: 3, decoys: 3, critters: 14, lifeMinMs: 1_200, lifeMaxMs: 1_700, foliage: 14 },
        { durationMs: 11_000, ripe: 6, unripe: 7, half: 5, decoys: 3, critters: 27, lifeMinMs: 900, lifeMaxMs: 1_400, foliage: 17 },
    ],
};
/** 한 판의 점수 상한 = 모든 라운드의 익은 토마토 합. 시드와 무관(개수는 설정이 정함). */
export function harvestMaxPicked(config) {
    return config.rounds.reduce((sum, r) => sum + r.ripe, 0);
}
/** 판 전체 길이(ms). 플레이 시간 검증에 쓴다(라운드 사이 인트로는 별도). */
export function harvestTotalDurationMs(config) {
    return config.rounds.reduce((sum, r) => sum + r.durationMs, 0);
}
/** 점수 = 수확한 익은 토마토 − 감점×오클릭. 음수는 0으로 자른다(리더보드 위생). */
export function harvestScore(config, picked, misclicks) {
    return Math.max(0, picked - config.penalty * misclicks);
}
/** 지급 토마토. 수확량 기준(감점은 점수에만 붙는다 — 지급까지 깎으면 너무 짜다). */
export function harvestReward(config, picked) {
    return Math.floor(Math.max(0, picked) * config.payoutRate);
}
/** 관리인이 떼가는 수수료 — 결과 화면 연출용. */
export function harvestFee(config, picked) {
    return Math.max(0, picked) - harvestReward(config, picked);
}
// ─────────────────────────────────────────────────────────────
// 결정론 스폰 생성
// ─────────────────────────────────────────────────────────────
/** mulberry32 — 시드 하나로 충분히 고른 32비트 PRNG. 서버·클라가 같은 수열을 얻는다. */
export function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** 겹침 방지 격자. 4×5 = 20칸 — 모바일 폭에서도 칸이 44px 터치 타깃보다 넉넉하다. */
const GRID_COLS = 4;
const GRID_ROWS = 5;
/**
 * 시드로부터 판 전체 스폰 플랜을 만든다. 같은 (seed, config)면 어디서 돌려도 같다.
 * 동시에 화면에 있는 것들끼리 같은 칸을 쓰지 않도록, 등장 순서대로 칸을 돌려쓴다.
 */
export function generateHarvestPlan(seed, config) {
    const rand = mulberry32(seed);
    return config.rounds.map((round, roundIdx) => {
        // 종류 배열을 만들어 섞는다 — 등장 순서에 종류가 고르게 섞이도록.
        const kinds = [
            ...Array.from({ length: round.ripe }, () => 'ripe'),
            ...Array.from({ length: round.unripe }, () => 'unripe'),
            ...Array.from({ length: round.half }, () => 'half'),
            ...Array.from({ length: round.decoys }, () => 'pepper'),
            ...Array.from({ length: round.critters }, () => 'critter'),
        ];
        for (let i = kinds.length - 1; i > 0; i -= 1) {
            const j = Math.floor(rand() * (i + 1));
            [kinds[i], kinds[j]] = [kinds[j], kinds[i]];
        }
        // 칸 순서도 시드로 섞고, 스폰마다 순서대로 소비한다(모듈로 순환).
        const cells = Array.from({ length: GRID_COLS * GRID_ROWS }, (_, i) => i);
        for (let i = cells.length - 1; i > 0; i -= 1) {
            const j = Math.floor(rand() * (i + 1));
            [cells[i], cells[j]] = [cells[j], cells[i]];
        }
        const n = kinds.length;
        const spawns = kinds.map((raw, i) => {
            // 생물은 무당벌레·나비 반반.
            const kind = raw === 'critter' ? (rand() < 0.5 ? 'ladybug' : 'butterfly') : raw;
            // 등장 시각: 라운드를 균등 분할하고 칸 안에서 지터 — 뭉치지도 비지도 않게.
            // 생물은 토마토보다 2배 오래 머문다 — 후반의 짧은 체류 시간에도 나는 게 보이도록.
            const lifeBase = round.lifeMinMs + rand() * (round.lifeMaxMs - round.lifeMinMs);
            const lifeMs = isCritter(kind) ? lifeBase * 2 : lifeBase;
            const windowMs = Math.max(1, round.durationMs - lifeMs);
            const slotWidth = windowMs / n;
            const appearAt = Math.min(windowMs, slotWidth * i + rand() * slotWidth);
            const cell = cells[i % cells.length];
            const col = cell % GRID_COLS;
            const row = Math.floor(cell / GRID_COLS);
            // 칸 안 지터는 20%까지만 — 이웃 칸과 최소 간격을 남긴다.
            const xPct = ((col + 0.5 + (rand() - 0.5) * 0.4) / GRID_COLS) * 100;
            const yPct = ((row + 0.5 + (rand() - 0.5) * 0.4) / GRID_ROWS) * 100;
            const spawn = {
                id: `r${roundIdx}-${i}`,
                kind,
                xPct: Math.round(xPct * 100) / 100,
                yPct: Math.round(yPct * 100) / 100,
                appearAt: Math.round(appearAt),
                lifeMs: Math.round(lifeMs),
                scale: Math.round((0.85 + rand() * 0.3) * 100) / 100,
            };
            // 생물은 사는 동안 밭을 가로질러 이동한다. 나비가 무당벌레보다 멀리 난다.
            if (isCritter(kind)) {
                const range = kind === 'butterfly' ? 44 : 24;
                spawn.driftXPct = Math.round((rand() - 0.5) * 2 * range * 100) / 100;
                spawn.driftYPct = Math.round((rand() - 0.5) * range * 100) / 100;
            }
            return spawn;
        });
        // 수풀: 아무 데나(격자 무관), 1/3쯤은 스폰 앞을 가린다(pointer-events는 통과).
        const foliage = Array.from({ length: round.foliage }, () => ({
            xPct: Math.round((4 + rand() * 92) * 100) / 100,
            yPct: Math.round((8 + rand() * 84) * 100) / 100,
            scale: Math.round((0.7 + rand() * 0.8) * 100) / 100,
            front: rand() < 0.35,
            flip: rand() < 0.5,
            swayDelay: Math.round(rand() * 30) / 10,
        }));
        return { durationMs: round.durationMs, spawns, foliage };
    });
}
//# sourceMappingURL=harvest.js.map